/**
 * RankingConditionControls — the strip shown when a row authors a `ranking`
 * node: [ Highest / Lowest / Top % / Bottom % ] [ percent ] [ Path | Flow ].
 * Percent shows only for top/bottom %; the pool toggle only for landers/offers
 * (paths always rank flow-wide). Mirrors FlowTagConditionRow's ranking editor.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowLayer, FlowTagRankingOp, FlowTagRankingScope, RuleNode } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';
import { NumberField } from '@/components/ui/form/input/NumberField';

import { RANKING_OP_OPTIONS, rankingNeedsPercent } from './condition-kind';
import { DEFAULT_RANKING_PERCENT, setRankingOp } from './condition-node';

type RankingNode = Extract<RuleNode, { kind: 'ranking' }>;

export interface RankingConditionControlsProps {
  node: RankingNode;
  layer: FlowLayer;
  onChange: (next: RuleNode) => void;
}

const SCOPES: { value: FlowTagRankingScope; label: string }[] = [
  { value: 'path', label: 'Path' },
  { value: 'flow', label: 'Flow' },
];

const RankingConditionControls: FC<RankingConditionControlsProps> = ({ node, layer, onChange }) => {
  const scope = node.scope ?? 'path';
  return (
    <>
      <select
        value={node.op}
        onChange={(e) => onChange(setRankingOp(node, e.target.value as FlowTagRankingOp))}
        aria-label="Ranking comparison"
        className="h-8 w-24 shrink-0 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {RANKING_OP_OPTIONS.map((o) => (
          <option key={o.op} value={o.op}>
            {o.label}
          </option>
        ))}
      </select>

      {rankingNeedsPercent(node.op) ? (
        <span className="inline-flex items-center gap-1">
          <NumberField
            value={node.percent ?? DEFAULT_RANKING_PERCENT}
            min={1}
            max={100}
            onChange={(percent) => onChange({ ...node, percent })}
            aria-label="Ranking percent"
            className="h-8 w-14 rounded-md border border-input bg-background px-2 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <span className="text-xs text-muted-foreground">%</span>
        </span>
      ) : null}

      {layer !== 'paths' ? (
        <div
          role="radiogroup"
          aria-label="Ranking pool"
          className="flex items-center gap-px rounded border border-border bg-muted/20 p-px"
        >
          {SCOPES.map((s) => {
            const active = scope === s.value;
            return (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onChange({ ...node, scope: s.value })}
                className={cn(
                  'h-7 rounded-sm px-2 text-[11px] font-semibold transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </>
  );
};

export default RankingConditionControls;
